import React from 'react'
import { ListBulletIcon } from '@heroicons/react/24/outline'
import { getAngle } from 'konva'
import _ from 'lodash'

// Components
import { EditIcon } from '../../components/EditIcon'
import { LocationIcon } from '../../components/LocationIcon'
import { OrganizationIcon } from '../../components/OrganizationIcon'
import { SuitcaseIcon } from '../../components/SuitcaseIcon'
import { QRIcon } from '../../components/QRIcon'
import { UserIcon } from '../../components/UserIcon'
import qrCodeImage from './qr-code-placeholder.png'

// Services
import { getBadgeImage } from '../../services/badges.service'

// Utils
import { DEFAULT_LIST_ATTRS } from '../../utils/badgeList'

export const IS_UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i

/**
 * Handles configuring a text element for a merge field.
 * @param {string} content Merge field label
 * @returns text element configuration
 */
const configureTextElement = (content, accessor) => ({
  attrs: {
    width: 200,
    height: 50,
    text: content,
    accessor,
    fontSize: 20,
    align: 'center',
  },
  className: 'Text',
})

export const PRESET_BLOCKS = [
  {
    name: 'Full Name',
    icon: <UserIcon className="h-5 stroke-white" />,
    element: configureTextElement('{{ First Name }} {{ Last Name }}', 'fullName'),
    className: 'Text',
    editable: false,
  },
  {
    name: 'Job Title',
    icon: <SuitcaseIcon className="h-5 stroke-white" />,
    element: configureTextElement('{{ Job Title }}', 'title'),
    className: 'Text',
    editable: false,
  },
  {
    name: 'Company',
    icon: <OrganizationIcon className="h-5 stroke-white" />,
    element: configureTextElement('{{ Company }}', 'companyName'),
    className: 'Text',
    editable: false,
  },
  {
    name: 'Location',
    icon: <LocationIcon className="h-5 stroke-white" />,
    element: configureTextElement('{{ Location }}', 'presetLocation'),
    className: 'Text',
    editable: false,
  },
  {
    name: 'App QR Code',
    icon: <QRIcon className="w-[18px] fill-white" />,
    element: {
      attrs: {
        width: 130,
        height: 130,
        src: qrCodeImage,
        qrImage: true,
      },
      className: 'Image',
    },
    className: 'Image',
    editable: false,
  },
]

export const CUSTOM_BLOCKS = [
  {
    name: 'Text',
    icon: <EditIcon className="h-5 fill-white" />,
    element: configureTextElement('Enter text here', null),
    className: 'Text',
    editable: true,
  },
  {
    name: 'Merge Field',
    icon: <EditIcon className="h-5 fill-white" />,
    element: configureTextElement('', null),
    className: 'Text',
    editable: false,
  },
  {
    name: 'Custom QR Code',
    icon: <QRIcon className="w-[18px] fill-white" />,
    element: {
      attrs: {
        width: 130,
        height: 130,
        src: qrCodeImage,
        qrImage: true,
        customQr: true,
        qrContent: '',
      },
      className: 'Image',
    },
    className: 'Image',
    editable: false,
  },
  {
    name: 'Rectangle',
    icon: <EditIcon className="h-5 fill-white" />,
    element: {
      attrs: {
        width: 100,
        height: 100,
      },
      className: 'Rect',
    },
    className: 'Rect',
    editable: false,
  },
  {
    name: 'Image',
    icon: <EditIcon className="h-5 fill-white" />,
    className: 'Image',
    editable: true,
  },
  {
    name: 'List',
    icon: <ListBulletIcon className="h-5 stroke-white" />,
    element: {
      attrs: { ...DEFAULT_LIST_ATTRS },
      className: 'List',
    },
    className: 'List',
    editable: false,
  },
]

/**
 * Handles finding the location of an element within the config object.
 * @param {object} config
 * @param {string} id
 * @returns location object
 */
export const findElementLocation = (config, id) => {
  const location = {
    panelIndex: null,
    layerIndex: null,
    groupElementIndex: null,
    elementIndex: null,
  }

  // If no `id` is provided, return the location object as is
  if (!id) return location

  // Loop through panels and layers to find the matching element
  _.forEach(_.keys(config), (panel, panelIndex) => {
    _.forEach(config[panel].children, (layer, layerIndex) => {
      // Matching layer return early
      if (layer.attrs.id === id) {
        location.panelIndex = panelIndex
        location.layerIndex = layerIndex
        return
      }

      // Find the element within the layer
      const element = _.findIndex(layer.children, (child) => {
        if (child.attrs.id === id) return true
        if (child.children)
          return _.findIndex(child.children, (groupChild) => groupChild.attrs.id === id) !== -1
        return false
      })

      // If the element is found, update the location object
      if (element !== -1) {
        location.panelIndex = panelIndex
        location.layerIndex = layerIndex
        location.elementIndex = element

        // If the element found is a group, find the nested element index
        if (config[`${panelIndex + 1}`].children[layerIndex].children[element].children) {
          const elementIndex = _.findIndex(
            config[`${panelIndex + 1}`].children[layerIndex].children[element].children,
            (groupChild) => groupChild.attrs.id === id,
          )

          if (elementIndex !== -1) {
            location.groupElementIndex = elementIndex
          }
        }
      }
    })
  })

  return location
}

/**
 * Handles rotating a point around the origin.
 * @param {object} position
 * @param {number} rad
 * @returns updated x and y positioning
 */
const rotatePoint = ({ x, y }, rad) => {
  const rcos = Math.cos(rad)
  const rsin = Math.sin(rad)
  return { x: x * rcos - y * rsin, y: y * rcos + x * rsin }
}

/**
 * Rotates the specified `node` around its center by the specified `rotation` angle.
 * @param {object} node
 * @param {number} rotation
 * @returns updated x and y positioning
 */
export const rotateAroundCenter = (node, rotation) => {
  // current rotation origin (0, 0) relative to desired origin - center (node.width()/2, node.height()/2)
  const topLeft = { x: -node.width() / 2, y: -node.height() / 2 }
  const current = rotatePoint(topLeft, getAngle(node.rotation()))
  const rotated = rotatePoint(topLeft, getAngle(rotation))
  const dx = rotated.x - current.x
  const dy = rotated.y - current.y

  return {
    x: node.x() + dx,
    y: node.y() + dy,
  }
}

/**
 * Pulls all fonts used in the badge.
 * @param {string} defaultFont
 * @param {object} configObject
 * @returns list of fonts used in the badge
 */
export const getGlobalFontsFromBadge = (defaultFont, configObject) => {
  const customFonts = []
  const googleFonts = []

  const isCustomFont = IS_UUID.test(defaultFont)

  // If the default font is not a custom font, add it to the list of Google Fonts
  if (!isCustomFont && defaultFont) {
    googleFonts.push({ font: defaultFont, weights: ['regular', 'italic', '700', '700italic'] })
  } else if (defaultFont) {
    customFonts.push(defaultFont)
  }

  /**
   * Records the font an element draws with, if it draws text at all.
   * Lists render text just like Text elements do, so they have to be collected
   * here too — otherwise a list-only badge paints before its font has loaded.
   * @param {object} element
   */
  const collectElementFont = (element) => {
    const drawsText = element.className === 'Text' || element.className === 'List'
    if (!drawsText || !element.attrs.fontFamily) return

    if (element.attrs.fontId) {
      customFonts.push(element.attrs.fontId)
    } else {
      googleFonts.push({
        font: element.attrs.fontFamily,
        weights: ['regular', 'italic', '700', '700italic'],
      })
    }
  }

  // Pull all nested fonts from the config
  _.forEach(configObject, (panel) => {
    _.forEach(panel.children, (layer) => {
      _.forEach(layer.children, (element) => {
        if (element.className === 'Group') {
          _.forEach(element.children, collectElementFont)
        } else {
          collectElementFont(element)
        }
      })
    })
  })

  return [_.uniqBy(googleFonts, 'font'), _.uniq(customFonts)]
}

/**
 * Loads Google Fonts by injecting a stylesheet, triggering font file downloads,
 * and priming the canvas font system.
 *
 * Three problems are solved here:
 * 1. react-google-font (GoogleFontLoader) has a double-mount bug that briefly removes
 *    its <link> from the DOM, invalidating @font-face declarations mid-load.
 * 2. Browsers only auto-download font files when they're used in visible DOM text —
 *    not canvas (Konva) — so we must explicitly trigger downloads.
 * 3. The canvas 2D context may not render a web font correctly on its very first draw
 *    even after document.fonts.load() resolves. Drawing throwaway text on an offscreen
 *    canvas "primes" the font system so Konva's first real draw is correct.
 *
 * @param {Array} fonts - Array of { font, weights } objects from getGlobalFontsFromBadge
 * @param {number} maxWaitMs - Maximum time to wait in milliseconds (default: 5000)
 * @returns {Promise} Resolves when all fonts are loaded and primed for canvas use
 */
export const waitForGoogleFonts = async (fonts, maxWaitMs = 5000) => {
  if (!fonts.length) return

  // Build the Google Fonts CSS URL (same format as react-google-font / v1 API)
  const families = fonts
    .map((f) => {
      const family = f.font.replace(/ +/g, '+')
      const weights = (f.weights || []).join(',')
      return family + (weights ? `:${weights}` : '')
    })
    .join('|')
  const href = `https://fonts.googleapis.com/css?family=${families}`

  // Only inject the link once for a given set of fonts
  if (!document.querySelector(`link[href="${href}"]`)) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    await new Promise((resolve) => {
      link.onload = resolve
      link.onerror = resolve
      document.head.appendChild(link)
      setTimeout(resolve, maxWaitMs)
    })
  }

  // Trigger actual font file downloads
  const fontNames = fonts.map((f) => f.font)
  await Promise.all(
    fontNames.flatMap((name) => [
      document.fonts.load(`16px "${name}"`).catch(() => {}),
      document.fonts.load(`bold 16px "${name}"`).catch(() => {}),
    ]),
  )

  // Prime the canvas font system — the browser's canvas 2D context may not render
  // a newly-loaded web font correctly on its first draw. Drawing throwaway text
  // forces the font system to fully register the font for canvas use.
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  fontNames.forEach((name) => {
    ctx.font = `16px "${name}"`
    ctx.fillText('.', 0, 0)
    ctx.font = `bold 16px "${name}"`
    ctx.fillText('.', 0, 0)
  })
}

export const getBadgeImages = async (badgeImages, configObject) => {
  // Pull out all images from badge
  const imagesOnBadge = {}
  _.forEach(configObject, (panel) => {
    _.forEach(panel.children, (layer) => {
      _.forEach(layer.children, (element) => {
        if (element.attrs.id === 'base') return

        if (badgeImages[element.attrs.id]) {
          imagesOnBadge[element.attrs.id] = badgeImages[element.attrs.id]
        } else if (element.className === 'Image' && !element.attrs.qrImage) {
          imagesOnBadge[element.attrs.id] = {
            url: element.attrs.badgeImagePath,
            base64: null,
          }
        }
      })
    })
  })

  // For tracked images where we don't have the base64 string, get it
  const images = _.filter(_.map(imagesOnBadge, (i, k) => !i.base64 && k))

  if (images.length > 0) {
    await Promise.all(
      _.map(images, async (id) => {
        const badgeImageData = await getBadgeImage(
          imagesOnBadge[id].url,
          () => {},
          () => {},
        )

        imagesOnBadge[id] = badgeImageData
      }),
    )
  }

  return imagesOnBadge
}
